/**
 * Status chip counts for the work-order list (#2881).
 *
 * The "all" chip shows the stats route's `totalCount`, which counts every row
 * whatever its status. The status chips only exist for the tenant's enabled
 * flow statuses, so a row in a legacy status (`confirmed`, `paid`) or a status
 * the tenant has since disabled was counted in the total with no chip of its
 * own — the chips summed to less than the total and the work order looked lost.
 *
 * `partitionStatusCounts` splits the stats into the counts the flow chips show
 * and the remainder, so the remainder can get a chip of its own. Invariant:
 * the sum of `listed` plus `other.count` equals `totalCount`.
 *
 * The derived approval wait (#3027) is not a `byStatus` row. Appending a second
 * `waiting_approval` row broke shipped iOS clients, which build a dictionary of
 * `byStatus` keyed by status, so the API reports it as the additive root field
 * `waitingApprovalCount` instead. It is not part of `totalCount`.
 */

/** A `byStatus` entry from `GET /work-orders/stats`. */
export interface StatusCountEntry {
  status: string;
  count: number;
}

export interface PartitionedStatusCounts {
  /** Count per enabled status key; every enabled key is present (0 when absent). */
  listed: Record<string, number>;
  /** Rows whose status has no chip. `statuses` lists only statuses with rows. */
  other: { statuses: string[]; count: number };
}

/**
 * The stats route appends `waiting_parts` after the real per-status rows. It
 * counts the `isWaitingForParts` flag, which sits alongside the row's real
 * status, so it is not part of `totalCount` (#2154).
 */
export const WAITING_PARTS_STATUS = 'waiting_parts';

/**
 * Derived approval wait (#3027). The list filter token `status=waiting_approval`
 * matches the derived flag or a legacy stored primary status of that name.
 * The stats route reports the count of that union as the root field
 * `waitingApprovalCount`; a legacy stored `waiting_approval` primary row stays in
 * `byStatus` like any other status.
 */
export const WAITING_APPROVAL_STATUS = 'waiting_approval';

/**
 * Whether a row gets the derived approval-wait marker next to its status. A row
 * whose primary status already is the legacy `waiting_approval` shows that as
 * its status badge, so it is not marked a second time.
 */
export function showsApprovalWaitMarker(row: {
  status?: string | undefined;
  isWaitingForApproval?: boolean | undefined;
}): boolean {
  return row.isWaitingForApproval === true && row.status !== WAITING_APPROVAL_STATUS;
}

/**
 * Text of the approval-wait marker: the translated status label, plus the
 * number of open requests when there is more than one. The count is a bare
 * number, so no locale key is needed.
 */
export function approvalWaitLabel(label: string, openApprovalCount: number | undefined): string {
  return openApprovalCount !== undefined && openApprovalCount > 1
    ? `${label} (${String(openApprovalCount)})`
    : label;
}

/** Selection key for the "other statuses" chip. Never a real status. */
export const OTHER_STATUSES_KEY = 'other';

export function partitionStatusCounts(
  byStatus: readonly StatusCountEntry[],
  enabledKeys: readonly string[],
): PartitionedStatusCounts {
  // The last `waiting_parts` entry is the flag count; a real row still carrying
  // the legacy status comes before it and is a status like any other.
  let waitingPartsIndex = -1;
  for (let i = byStatus.length - 1; i >= 0; i -= 1) {
    if (byStatus[i]?.status === WAITING_PARTS_STATUS) {
      waitingPartsIndex = i;
      break;
    }
  }

  const enabled = new Set(enabledKeys);
  const listed: Record<string, number> = {};
  for (const key of enabledKeys) listed[key] = 0;

  const otherCounts = new Map<string, number>();
  byStatus.forEach((entry, index) => {
    if (index === waitingPartsIndex) return;
    if (enabled.has(entry.status)) {
      listed[entry.status] = (listed[entry.status] ?? 0) + entry.count;
    } else if (entry.count > 0) {
      otherCounts.set(entry.status, (otherCounts.get(entry.status) ?? 0) + entry.count);
    }
  });

  let otherCount = 0;
  for (const count of otherCounts.values()) otherCount += count;

  return {
    listed,
    other: { statuses: [...otherCounts.keys()], count: otherCount },
  };
}
